import { useVirtualizer } from '@tanstack/react-virtual';
import { useAppSelector } from '../../stores/store';
import { useContext, useEffect, useRef, useState, MouseEvent } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import EpgPageHelpers from '../../helpers/epgPageHelpers';
import PlayButton from '../buttons/PlayButton';
import { AppMainStateContext } from '../../contexts/AppMainStateContext';
import EpgPageTimeLine from './EpgPageTimeLine';
import { EpgItem } from '../epg/EpgItem';

interface IProps {
    timelineHoursLength: number,
    hourInPixel: number
}

export const EpgPageEpgsList = ({ hourInPixel, timelineHoursLength }: IProps) => {
    const isAvailableInBMGOFilterOn = useAppSelector(state => state.epgPage.isAvailableInBMGOFilterOn);
    const searchInputValue = useAppSelector(state => state.epgPage.searchInputValue);
    const channels = useAppSelector(state => state.epgPage.channels).filter((x) =>
        x.name
            .toLowerCase()
            .includes(searchInputValue.toLowerCase()))
        .filter((x) => (isAvailableInBMGOFilterOn ? x.enabled : true))
        .sort((x, y) => x.position - y.position);
    const timelineHours = useAppSelector(state => state.epgPage.timelineHours);
    const firstHourTimeline = useAppSelector(state => state.epgPage.timelineHours[0]);
    const lastHourTimeline = [...useAppSelector(state => state.epgPage.timelineHours)].sort((a, b) => b.timestamp - a.timestamp)[0];
    const lastHourWithDownloadPoint = useAppSelector(state => state.epgPage.timelineHours.filter(x => x.downloadPoint)).sort((a, b) => b.timestamp - a.timestamp)[0];
    const lastEpgSegment = [...useAppSelector(state => state.epgPage.epgSegments)].sort((a, b) => b.fromTimestamp - a.fromTimestamp)[0];
    const parentRef = useRef<HTMLDivElement>(null);
    const isTimelineLoaded = useAppSelector(state => state.epgPage.isTimelineLoaded);


    const { isMobile } = useContext(AppMainStateContext);

    const [isDown, setIsDown] = useState(false);
    const [startX, setStartX] = useState(0);
    const [startY, setStartY] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [scrollTop, setScrollTop] = useState(0);
    const [_, setElapsedTimeMaskWidth] = useState(0);


    const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
        const scrollContainer = parentRef.current;
        setIsDown(true);
        if (scrollContainer) {
            setStartX(e.pageX - scrollContainer?.offsetLeft);
            setStartY(e.pageY - scrollContainer?.offsetTop);
            setScrollLeft(scrollContainer?.scrollLeft);
            setScrollTop(scrollContainer?.scrollTop);
        }
    };

    const handleMouseLeave = () => {
        setIsDown(false);
    };

    const handleMouseUp = () => {
        setIsDown(false);
    };

    const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
        if (!isDown) return;
        e.preventDefault();
        const scrollContainer = parentRef.current;
        if (scrollContainer) {
            const x = e.pageX - scrollContainer.offsetLeft;
            const y = e.pageY - scrollContainer.offsetTop;
            const walkX = (x - startX) * 2; // Prędkość przewijania w osi X
            const walkY = (y - startY) * 2; // Prędkość przewijania w osi Y
            scrollContainer.scrollLeft = scrollLeft - walkX;
            scrollContainer.scrollTop = scrollTop - walkY;
        }
    };

    const getMaskWidthToActualTime = (fromTimestamp: number, hourInPixel: number): number => {
        const iconLogoWidth = 64;
        const dateTimeNow = new Date();
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        const currentUnixTimestamp = Math.floor(dateTimeNow.getTime() / 1000);

        return (
            ((currentUnixTimestamp - fromTimestamp) / 60) *
            (1 / (60 / hourInPixel)) +
            iconLogoWidth
        );
    };

    const rowVirtualizer = useVirtualizer({
        count: channels.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => isMobile ? 48 : 58,
    });

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isTimelineLoaded) {
            interval = setInterval(() => {
                setElapsedTimeMaskWidth(getMaskWidthToActualTime(timelineHours[0].timestamp, hourInPixel));
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isTimelineLoaded, timelineHours]);


    return (
        <div
            className={`h-[calc(100%-110px)] md:h-[calc(100vh-56px)] relative z-0`}
            id={'scroller'}
            ref={parentRef}
            style={{
                overflow: 'auto',
            }}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
        >
            <EpgPageTimeLine />
            <div
                style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                }}
            >
                {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                    const channel = channels[virtualItem.index];
                    return <div
                        key={virtualItem.key}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: timelineHoursLength * hourInPixel + 'px',
                            height: `${virtualItem.size}px`,
                            transform: `translateY(${virtualItem.start}px)`,
                        }}
                    >
                        <div
                            className={'animate-opacityEaseIna'}
                            style={{ display: 'flex', flexDirection: 'row', width: '100%' }}
                            key={virtualItem.key + 'row'}
                        >
                            <PlayButton
                                channel={{ ch_id: channel.ch_id, enabled: channel.enabled, name: channel.name }}
                                withUnavailableModal={false}
                                className={`${isMobile ? 'h-12' : 'h-14'} w-18 sticky left-0 flex justify-end bg-neutral-800 select-none px-2 z-20 shrink-0`}
                                loggedInElem={
                                    <LazyLoadImage
                                        alt=""
                                        effect="opacity"
                                        className={`${isMobile ? 'h-12 w-10' : 'h-14 w-12'} py-1 align-middle`}
                                        wrapperClassName={'place-content-center'}
                                        src={`${import.meta.env.VITE_MIX_CHANNEL_LOGO_URL}/${channels[virtualItem.index].logo}`}
                                    />
                                }
                                loggedOutElem={
                                    <LazyLoadImage
                                        alt=""
                                        effect="opacity"
                                        className={`${isMobile ? 'h-12 w-10' : 'h-14 w-12'} py-1 align-middle`}
                                        wrapperClassName={'place-content-center h-full'}
                                        src={`${import.meta.env.VITE_MIX_CHANNEL_LOGO_URL}/${channels[virtualItem.index].logo}`}
                                    />
                                }
                            />
                            {channel?.epgList.map((epg, i) => {
                                    const isLastElem = channels[virtualItem.index].epgList.length === i + 1;
                                    return <span key={epg.id} className="flex">
                                    {i === 0 && epg.start_timestamp > firstHourTimeline.timestamp &&
                                        <div
                                            key={virtualItem.key + 'icon'}
                                            className="animate-pulse h-13 bg-neutral-700 mr-1 my-0.5 border border-transparent shrink-0"
                                            style={{
                                                width:
                                                    EpgPageHelpers.timeStampStartAndStopToWidthConverter(
                                                        firstHourTimeline.timestamp,
                                                        epg.start_timestamp,
                                                        firstHourTimeline.timestamp,
                                                        hourInPixel,
                                                    ),
                                            }}
                                        />
                                    }
                                        {epg.stop_timestamp > firstHourTimeline.timestamp &&
                                            <EpgItem
                                                key={`${channels[virtualItem.index].ch_id}.${epg.id}.${i * i}`}
                                                channelId={channels[virtualItem.index].ch_id}
                                                channelName={channels[virtualItem.index].name}
                                                epgId={epg.id}
                                                name={epg.name}
                                                startTimestamp={epg.start_timestamp}
                                                endTimestamp={epg.stop_timestamp}
                                                description={epg.descr}
                                                handleEpgItemClick={() => {
                                                }}
                                                hourInPixels={hourInPixel}
                                                index={i}
                                            />
                                        }
                                        {isLastElem && epg.stop_timestamp < lastHourWithDownloadPoint.timestamp &&
                                            <div
                                                className="animate-pulse h-13 bg-neutral-700 mr-1 my-0.5 border border-transparent shrink-0"
                                                style={{
                                                    width:
                                                        EpgPageHelpers.timeStampStartAndStopToWidthConverter(
                                                            epg.stop_timestamp,
                                                            lastHourTimeline.timestamp + 4200,
                                                            firstHourTimeline.timestamp,
                                                            hourInPixel,
                                                        ),
                                                }}
                                            />
                                        }
                                        {isLastElem && lastEpgSegment.isSegmentDownloaded === true && channel.lastEpgId === epg.id &&
                                            <div
                                                className="flex h-13  mr-1 my-0.5 border border-neutral-500 shrink-0 items-center pl-1 sticky left-10"
                                                style={{
                                                    width:
                                                        EpgPageHelpers.timeStampStartAndStopToWidthConverter(
                                                            epg.stop_timestamp,
                                                            lastHourTimeline.timestamp + 4200,
                                                            firstHourTimeline.timestamp,
                                                            hourInPixel,
                                                        ),
                                                }}
                                            >Brak więcej danych</div>}
                                </span>;
                                },
                            )}
                        </div>
                    </div>;
                })}
            </div>
        </div>
    );
};